import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Stream from 'flarum/common/utils/Stream';
import associationsField from './utils/associationsField';
import addProfileFieldItems from './utils/addProfileFieldItems';

// See addRegistrationTerms.js for why this is a string, not a direct
// import: SignUpModal is only ever opened via a lazy `import()` in core,
// so it may not be loaded yet when this extension's own bundle runs.
const SIGN_UP_MODAL = 'flarum/forum/components/SignUpModal';

/**
 * Adds five optional fields to SignUpModal - Prénom, Nom, Localisation,
 * Site web perso (plain text inputs) and Association(s) (a repeatable list
 * of name+link pairs, starting empty since none of this is required - see
 * utils/associationsField.js, shared with the settings-page equivalent of
 * this same field). Submitted the same way flarum-nicknames submits its
 * own `nickname` field for registration: a plain writable UserResource
 * field (see extend.php), added to the /register request body via
 * `submitData` below - no separate endpoint or validation step needed for
 * this to reach the server.
 *
 * And lays the whole form out: two sections - the account (what signing up
 * needs), then the public profile (all optional) - with every optional
 * field marked "(optionnel)", in a wider modal than core's Modal--small, two
 * columns from tablet up (see champis-theme's .ChampisSignUpModal): one
 * narrow column of eleven fields made it look like a chore.
 */
export default function addRegistrationFields() {
  override(SIGN_UP_MODAL, 'className', function (original) {
    return original().replace('Modal--small', 'ChampisSignUpModal');
  });
  extend(SIGN_UP_MODAL, 'oninit', function () {
    this.champisFirstName = Stream('');
    this.champisLastName = Stream('');
    this.champisLocation = Stream('');
    this.champisWebsite = Stream('');
    this.champisAssociations = [];
  });
  extend(SIGN_UP_MODAL, 'fields', function (items) {
    const t = key => app.translator.trans(`champis-users.forum.sign_up.${key}`);
    // A new vnode each time: one can't be rendered in several places.
    const optional = () => m("span", {
      className: "ChampisSignUpModal-optional"
    }, t('optional'));
    items.add('champisAccountSection', section(t('account_heading')), 40);

    // flarum-nicknames' own field: optional unless usernames are random
    // (then it's the only name, and required - see its `required`).
    if (items.has('nickname') && !app.forum.attribute('randomizeUsernameOnRegistration')) {
      appendToLabel(items.get('nickname'), optional());
    }
    items.add('champisProfileSection', section(t('profile_heading'), t('profile_hint')), 6);
    addProfileFieldItems(items, {
      firstName: this.champisFirstName,
      lastName: this.champisLastName,
      location: this.champisLocation,
      website: this.champisWebsite,
      disabled: this.loading,
      priority: 5,
      labelSuffix: optional
    });
    items.add('champisAssociations', associationsField(this.champisAssociations, {
      disabled: this.loading,
      labelSuffix: optional
    }), 1);
  });
  extend(SIGN_UP_MODAL, 'submitData', function (data) {
    if (this.champisFirstName()) data.champisFirstName = this.champisFirstName();
    if (this.champisLastName()) data.champisLastName = this.champisLastName();
    if (this.champisLocation()) data.champisLocation = this.champisLocation();
    if (this.champisWebsite()) data.champisWebsite = this.champisWebsite();
    const associations = this.champisAssociations.filter(association => association.name || association.url);
    if (associations.length) data.champisAssociations = associations;
  });
}
function section(heading, hint) {
  return m("div", {
    className: "ChampisSignUpModal-section"
  }, m("h4", null, heading), hint && m("p", null, hint));
}

/**
 * Adds `suffix` to the <label> of another extension's Form-group - built
 * before this runs, as a plain vnode tree (see flarum-nicknames' own
 * `fields` extension): not rendered yet, so still free to change.
 */
function appendToLabel(group, suffix) {
  const children = group?.children;
  const label = Array.isArray(children) ? children.find(child => child?.tag === 'label') : undefined;
  if (!label) return;
  label.children = [...(Array.isArray(label.children) ? label.children : [label.text ?? label.children]), suffix];
  label.text = undefined;
}